import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Users, Search, Star, MapPin, Phone, Mail,
  ExternalLink, Trash2, Filter, Loader2,
  Download, Upload, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MyLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    contacted: '',
    search: ''
  });
  const [total, setTotal] = useState(0);
  
  // CSV Import/Export state
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('lead_status', filters.status);
      if (filters.contacted !== '') params.append('contacted', filters.contacted);

      const response = await axios.get(`${API_URL}/leads/saved?${params}`);
      setLeads(response.data.leads || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch leads');
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.contacted]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // CSV Export function
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/leads/export/csv`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nexus_leads_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Leads exported successfully!');
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('No leads to export');
      } else {
        toast.error('Failed to export leads');
      }
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };

  // CSV Import function
  const handleImportCSV = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/leads/import/csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { imported, skipped, errors } = response.data;
      
      if (imported > 0) {
        toast.success(`Imported ${imported} leads successfully!`);
        fetchLeads(); // Refresh the list
      }
      
      if (skipped > 0) {
        toast.info(`${skipped} leads skipped (duplicates or missing data)`);
      }
      
      if (errors && errors.length > 0) {
        toast.error(`${errors.length} errors occurred during import`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import leads');
      console.error('Import error:', error);
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;

    try {
      await axios.delete(`${API_URL}/leads/${leadId}`);
      toast.success('Lead deleted');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      new: 'badge-primary',
      contacted: 'badge-success',
      follow_up: 'badge-warning',
      replied: 'badge-success',
      closed: 'badge-neutral'
    };
    return badges[status] || 'badge-neutral';
  };

  const filteredLeads = leads.filter(lead => {
    if (!filters.search) return true;
    const search = filters.search.toLowerCase();
    return (
      lead.business_name?.toLowerCase().includes(search) ||
      lead.niche?.toLowerCase().includes(search) ||
      lead.city?.toLowerCase().includes(search)
    );
  });

  return (
    <DashboardLayout>
      <Header 
        title="My Leads" 
        subtitle={`${total} leads saved`}
      />
      
      <div className="p-8" data-testid="my-leads-content">
        {/* Filters */}
        <div className="card-dashboard mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-600">Filters:</span>
            </div>
            
            <div className="w-48">
              <Select
                value={filters.status || 'all'}
                onValueChange={(value) => setFilters({ ...filters, status: value === 'all' ? '' : value })}
              >
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="replied">Replied</SelectItem>
                  <SelectItem value="follow_up">Follow Up</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="w-48">
              <Select
                value={filters.contacted || 'all'}
                onValueChange={(value) => setFilters({ ...filters, contacted: value === 'all' ? '' : value })}
              >
                <SelectTrigger data-testid="contacted-filter">
                  <SelectValue placeholder="All Leads" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Leads</SelectItem>
                  <SelectItem value="true">Contacted</SelectItem>
                  <SelectItem value="false">Not Contacted</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search leads..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* CSV Import/Export Buttons */}
            <div className="flex items-center gap-2 ml-auto">
              <input
                type="file"
                accept=".csv"
                onChange={handleImportCSV}
                ref={fileInputRef}
                className="hidden"
                id="csv-import"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                data-testid="import-csv-btn"
              >
                {importing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Import CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                disabled={exporting || total === 0}
                data-testid="export-csv-btn"
              >
                {exporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Export CSV
              </Button>
            </div>
          </div>
        </div>

        {/* Leads Table */}
        {loading ? (
          <div className="card-dashboard text-center py-16">
            <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mx-auto mb-4" />
            <p className="text-slate-500">Loading leads...</p>
          </div>
        ) : filteredLeads.length > 0 ? (
          <div className="card-dashboard overflow-hidden p-0">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Business</th>
                  <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Contact</th>
                  <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Location</th>
                  <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Rating</th>
                  <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Contacted</th>
                  <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((lead) => (
                  <tr key={lead.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="py-4 px-6">
                      <div className="font-medium text-slate-900">{lead.business_name}</div>
                      <div className="text-sm text-slate-500">{lead.niche}</div>
                    </td>
                    <td className="py-4 px-6">
                      {lead.email && (
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <Mail className="w-3 h-3" />
                          {lead.email}
                        </div>
                      )}
                      {lead.phone && (
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <Phone className="w-3 h-3" />
                          {lead.phone}
                        </div>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <MapPin className="w-3 h-3" />
                        {lead.city}, {lead.state}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        <span className="text-slate-700">{lead.google_rating || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className={getStatusBadge(lead.status)}>
                        {lead.status?.replace('_', ' ') || 'New'}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      {lead.contacted ? (
                        <span className="badge-success">Yes</span>
                      ) : (
                        <span className="badge-neutral">No</span>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/leads/${lead.id}`}>
                          <Button variant="ghost" size="sm" data-testid={`view-${lead.id}`}>
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteLead(lead.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-${lead.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="card-dashboard text-center py-16">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No leads found</h3>
            <p className="text-slate-500 mb-4">
              {filters.status || filters.contacted || filters.search
                ? 'Try adjusting your filters'
                : 'Start by searching for leads and saving them to your CRM'}
            </p>
            <Link to="/search">
              <Button className="btn-primary" data-testid="find-leads-btn">
                <Search className="w-4 h-4 mr-2" />
                Find Leads
              </Button>
            </Link>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MyLeads;
